import { type FC, type ChangeEvent } from 'react'
import cn from 'classnames'
import { EmailIconBlue } from '@/root/ui/shared/icons/EmailIconBlue'
import { IdKaartIcon } from '@/root/ui/tuuleliinid/icons/IdKaartIcon'
import { MobiilIdIcon } from '@/root/ui/tuuleliinid/icons/MobiilIdIcon'
import { SmartIdIcon } from '@/root/ui/tuuleliinid/icons/SmartIdIcon'
import { useGetTranslations } from '@/root/shared-hooks'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { TuuleliinidAuthMethod } from '@/root/modules/tuuleliinid/LoginPage/constants'
import { AuthMethodSelectorInput, AuthMethodSelectorLabel } from '@/root/modules/shared/LoginPage'

interface AuthMethodSelectorProps {
  authMethod: TuuleliinidAuthMethod
  onAuthMethodChange: (event: ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
  allowedMethods?: TuuleliinidAuthMethod[]
}

const AUTH_METHODS: { value: TuuleliinidAuthMethod; iconComponent: FC<{ width: number; height: number; color?: string }>; labelKey: Key; isColoredIcon: boolean }[] = [
  { value: 'email', iconComponent: EmailIconBlue, labelKey: 'authMethodEpost', isColoredIcon: false },
  { value: 'id-kaart', iconComponent: IdKaartIcon, labelKey: 'authMethodIdKaart', isColoredIcon: true },
  { value: 'mobiil-id', iconComponent: MobiilIdIcon, labelKey: 'authMethodMobiilId', isColoredIcon: true },
  { value: 'smart-id', iconComponent: SmartIdIcon, labelKey: 'authMethodSmartId', isColoredIcon: true },
]

export const AuthMethodSelector: FC<AuthMethodSelectorProps> = ({ authMethod, onAuthMethodChange, disabled, allowedMethods }) => {
  const { getTranslation } = useGetTranslations()
  const methods = allowedMethods ? AUTH_METHODS.filter(({ value }) => allowedMethods.includes(value)) : AUTH_METHODS

  return (
    <form className={cn('grid w-full max-w-2xl gap-4', methods.length <= 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4')}>
      <fieldset disabled={disabled} className={cn(disabled && 'opacity-50', 'contents')}>
      {methods.map(({ value, iconComponent: IconComponent, labelKey, isColoredIcon }) => {
        const isSelected = authMethod === value
        return (
          <AuthMethodSelectorLabel
            key={value}
            className={cn(
              'cursor-pointer rounded-xl border-2 px-4 py-5 transition-colors',
              isSelected
                ? 'border-secondary bg-secondary-lightest'
                : 'border-neutral-very-light bg-white hover:border-neutral-normal-lighter'
            )}
          >
            <AuthMethodSelectorInput value={value} checked={isSelected} onChange={onAuthMethodChange} />
            <div className="flex h-7 items-center justify-center">
              <IconComponent
                width={24}
                height={24}
                {...(!isColoredIcon ? { color: isSelected ? 'primary-base' : 'base-normal-light' } : {})}
              />
            </div>
            <p
              className={cn(
                'mt-1.5 text-center text-xs font-bold',
                isSelected ? 'text-success' : 'text-neutral-darkest'
              )}
            >
              {getTranslation(labelKey)}
            </p>
          </AuthMethodSelectorLabel>
        )
      })}
      </fieldset>
    </form>
  )
}
